import fontkit from "@pdf-lib/fontkit";
import { PDFDocument, rgb } from "pdf-lib";

const formFontUrl = require("dejavu-fonts-ttf/ttf/DejaVuSans.ttf");

export const GENERATED_SUBMISSION_BANKS = new Set(["mbank", "slsp", "tatra", "vub"]);

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 36;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const LABEL_WIDTH = 194;
const FIELD_GAP = 10;
const FIELD_WIDTH = CONTENT_WIDTH - LABEL_WIDTH - FIELD_GAP;
const ROW_HEIGHT = 27;
const FIELD_HEIGHT = 18;

const SKIPPED_KEYS = new Set([
  "id",
  "created",
  "modified",
  "cloud",
  "client",
  "clientInfo",
  "dti",
  "meta",
  "bank",
  "application_documents",
  "stage_history",
  "logo",
  "logo_square",
  "images",
  "profile_image",
  "has_google_account",
  "financnik",
  "is_staff",
  "is_active",
  "is_temporary",
  "role",
  "mortgage",
  "user",
  "url",
]);

const LABELS = {
  degree: "Titul",
  first_name: "Meno",
  last_name: "Priezvisko",
  email: "E-mail",
  telephone: "Telefón",
  datum_narodenia: "Dátum narodenia",
  rodne_cislo: "Rodné číslo",
  statna_prislusnost: "Štátna príslušnosť",
  op_cislo: "Číslo dokladu totožnosti",
  op_vytvoreny: "Doklad vydaný",
  op_platnost_do: "Platnosť dokladu do",
  rodinny_stav: "Rodinný stav",
  vzdelanie: "Vzdelanie",
  pocet_vyzivovanych_osob: "Počet vyživovaných osôb",
  pocet_nezaopatrenych_deti: "Počet nezaopatrených detí",
  aktualne_byvate: "Spôsob bývania",
  permanent_address_city: "Trvalý pobyt – obec",
  permanent_address_street: "Trvalý pobyt – ulica",
  permanent_address_popisne: "Trvalý pobyt – súpisné číslo",
  permanent_address_number: "Trvalý pobyt – orientačné číslo",
  permanent_address_zip: "Trvalý pobyt – PSČ",
  correspondence_address_city: "Korešpondenčná adresa – obec",
  correspondence_address_street: "Korešpondenčná adresa – ulica",
  correspondence_address_popisne: "Korešpondenčná adresa – súpisné číslo",
  correspondence_address_number: "Korešpondenčná adresa – orientačné číslo",
  correspondence_address_zip: "Korešpondenčná adresa – PSČ",
  suma_nadobudnutie: "Suma na nadobudnutie",
  suma_refinancovanie: "Suma na refinancovanie",
  suma_bezucel: "Bezúčelová časť",
  own_funds: "Vlastné zdroje",
  splatnost: "Splatnosť v rokoch",
  splatnost_mesiace: "Splatnosť v mesiacoch",
  fix: "Fixácia v rokoch",
  sadzba_pozadovana: "Požadovaná sadzba",
  sadzba_schvalena: "Schválená sadzba",
  splatka: "Mesačná splátka",
  preferred_payment_day: "Preferovaný deň splátky",
  sposob_cerpania: "Spôsob / počet čerpaní",
  cerpanie_na_plombu: "Čerpanie na plombu",
  hlavny_ucel: "Hlavný účel",
  vedlajsi_ucel: "Vedľajší účel",
  application_note: "Poznámka k žiadosti",
  druh: "Druh príjmu",
  typ: "Typ",
  suma: "Suma",
  vyska_splatky: "Mesačná splátka",
  povodna_suma: "Pôvodná výška úveru",
  zostatok: "Aktuálny zostatok",
  banka: "Banka / veriteľ",
  zamestnanie_od: "Príjem od",
  zamestnanie_do: "Príjem do",
  employment_type: "Pracovný pomer",
  vypovedna_skusobna_doba: "Skúšobná alebo výpovedná doba",
  pracovne_zaradenie: "Pracovné zaradenie",
  zamestnavatel_odvetvie: "Odvetvie",
  profesia: "Profesia",
  podiel_v_sro: "Podiel v spoločnosti",
  zisk_sro: "Zisk spoločnosti",
  minulorocny_obrat: "Ročný obrat / tržby",
  dan_z_prijmu: "Daň z príjmu",
  komora: "Profesijná komora",
  name: "Názov",
  ico: "IČO",
  address_city: "Obec",
  address_street: "Ulica",
  address_popisne: "Súpisné číslo",
  address_number: "Orientačné číslo",
  address_zip: "PSČ",
  address_text_city: "Obec",
  address_text_street: "Ulica",
  address_text_popisne: "Súpisné číslo",
  address_text_number: "Orientačné číslo",
  address_text_zip: "PSČ",
  hodnota: "Hodnota nehnuteľnosti",
  majitel: "Vlastník",
  child_type: "Druh nehnuteľnosti",
};

function humanizeKey(key) {
  const baseKey = key.split(".").pop().replace(/\[\d+\]/g, "");
  if (LABELS[baseKey]) return LABELS[baseKey];
  const label = baseKey.replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function stringifyValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Áno" : "Nie";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function flattenObject(value, prefix = "", output = []) {
  if (!value || typeof value !== "object") return output;

  Object.entries(value).forEach(([key, item]) => {
    if (SKIPPED_KEYS.has(key)) return;
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(item)) {
      if (item.every((entry) => entry === null || typeof entry !== "object")) {
        output.push({ key: path, label: humanizeKey(path), value: item });
      } else {
        item.forEach((entry, index) => flattenObject(entry, `${path}[${index + 1}]`, output));
      }
      return;
    }
    if (item && typeof item === "object") {
      flattenObject(item, path, output);
      return;
    }
    output.push({
      key: path,
      label: humanizeKey(path),
      value: item,
      isBoolean: typeof item === "boolean",
    });
  });

  return output;
}

function clientSections(role, data) {
  if (!data?.clientObject) return [];
  const sections = [
    { title: role, rows: flattenObject(data.clientObject) },
  ];
  (data.incomes || []).forEach((income, index) => {
    sections.push({
      title: `${role} – príjem ${index + 1}`,
      rows: flattenObject(income),
    });
  });
  (data.debts || []).forEach((debt, index) => {
    sections.push({
      title: `${role} – záväzok ${index + 1}`,
      rows: flattenObject(debt),
    });
  });
  return sections;
}

function fitText(text, font, size, maxWidth) {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  let shortened = text;
  while (shortened.length > 3 && font.widthOfTextAtSize(`${shortened}…`, size) > maxWidth) {
    shortened = shortened.slice(0, -1);
  }
  return `${shortened}…`;
}

export function shouldGenerateSubmissionOverview(bankCode) {
  return GENERATED_SUBMISSION_BANKS.has(bankCode);
}

export async function buildSubmissionOverviewPdf({
  application,
  applicantData,
  coApplicantData,
  foundedPropertiesData = [],
  financedPropertiesData = [],
}) {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const fontResponse = await fetch(formFontUrl);
  if (!fontResponse.ok) {
    throw new Error("Nepodarilo sa načítať písmo pre podacie PDF.");
  }
  const font = await pdfDoc.embedFont(await fontResponse.arrayBuffer(), { subset: true });
  const form = pdfDoc.getForm();
  const sections = [
    {
      title: "Údaje k podaniu",
      rows: [
        { label: "Číslo žiadosti v banke", value: "" },
        { label: "Pobočka / kontaktná osoba", value: "" },
        { label: "Interné číslo klienta", value: "" },
        { label: "Dátum podania", value: "" },
        { label: "Poznámky a chýbajúce prílohy", value: "", multiline: true },
      ],
    },
    { title: "Parametre hypotéky", rows: flattenObject(application) },
    ...clientSections("Žiadateľ", applicantData),
    ...clientSections("Spolužiadateľ", coApplicantData),
    ...(financedPropertiesData || []).map((property, index) => ({
      title: `Financovaná nehnuteľnosť ${index + 1}`,
      rows: flattenObject(property),
    })),
    ...(foundedPropertiesData || []).map((property, index) => ({
      title: `Zakladaná nehnuteľnosť ${index + 1}`,
      rows: flattenObject(property),
    })),
  ].filter((section) => section.rows.length > 0);

  let page;
  let y;
  let pageNumber = 0;
  let fieldIndex = 0;

  const addPage = () => {
    page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    pageNumber += 1;
    page.drawText("BANKARI – PODACÍ PREHĽAD K HYPOTEKÁRNEJ ŽIADOSTI", {
      x: MARGIN,
      y: PAGE_HEIGHT - 44,
      size: 13,
      font,
      color: rgb(0.06, 0.18, 0.34),
    });
    page.drawText(application?.bank?.nazov || "Banka", {
      x: MARGIN,
      y: PAGE_HEIGHT - 62,
      size: 9,
      font,
      color: rgb(0.32, 0.4, 0.5),
    });
    page.drawText(`Strana ${pageNumber}`, {
      x: PAGE_WIDTH - MARGIN - 52,
      y: 20,
      size: 8,
      font,
      color: rgb(0.45, 0.5, 0.58),
    });
    y = PAGE_HEIGHT - 88;
  };

  const ensureSpace = (height) => {
    if (!page || y - height < 42) addPage();
  };

  addPage();
  page.drawText(
    "Vyplniteľný pracovný podklad pre banky s online podaním. Údaje možno pred uložením ručne upraviť.",
    { x: MARGIN, y, size: 8, font, color: rgb(0.28, 0.34, 0.42) },
  );
  y -= 26;

  sections.forEach((section) => {
    ensureSpace(42);
    page.drawRectangle({
      x: MARGIN,
      y: y - 20,
      width: CONTENT_WIDTH,
      height: 22,
      color: rgb(0.9, 0.94, 0.98),
    });
    page.drawText(section.title, {
      x: MARGIN + 8,
      y: y - 14,
      size: 10,
      font,
      color: rgb(0.06, 0.18, 0.34),
    });
    y -= 31;

    section.rows.forEach((row) => {
      const multiline = row.multiline || stringifyValue(row.value).length > 110;
      const rowHeight = multiline ? 52 : ROW_HEIGHT;
      ensureSpace(rowHeight);
      const label = fitText(row.label, font, 8, LABEL_WIDTH - 8);
      page.drawText(label, {
        x: MARGIN,
        y: y - 12,
        size: 8,
        font,
        color: rgb(0.17, 0.22, 0.29),
      });

      fieldIndex += 1;
      const fieldName = `bankari_field_${fieldIndex}`;
      const fieldX = MARGIN + LABEL_WIDTH + FIELD_GAP;
      if (row.isBoolean) {
        const checkbox = form.createCheckBox(fieldName);
        checkbox.addToPage(page, {
          x: fieldX,
          y: y - 16,
          width: 13,
          height: 13,
          borderColor: rgb(0.38, 0.49, 0.63),
          borderWidth: 0.7,
        });
        if (row.value) checkbox.check();
      } else {
        const textField = form.createTextField(fieldName);
        textField.setText(stringifyValue(row.value));
        if (multiline) textField.enableMultiline();
        textField.addToPage(page, {
          x: fieldX,
          y: y - rowHeight + 7,
          width: FIELD_WIDTH,
          height: rowHeight - 9,
          font,
          borderColor: rgb(0.65, 0.72, 0.8),
          backgroundColor: rgb(0.99, 0.995, 1),
          borderWidth: 0.6,
        });
        textField.setFontSize(8);
      }
      y -= rowHeight;
    });
    y -= 8;
  });

  form.updateFieldAppearances(font);
  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type: "application/pdf" });
}
